import React, { FC, memo, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import * as Styled from './OCountdownEyebrowBanner.styles';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ABody, ABodyColor, ABodySize, MTimer } from '@marriott/mi-web-ui-kit';
import {
  getAccessiblePromoText,
  getAccessibleTimerAnnouncement,
  isTargetDateExpired,
} from './OCountdownEyebrowBanner.utils';
import { OCountdownEyebrowBannerPresentationProps as OffersCountdownEyebrowBannerPresentationProps } from './OCountdownEyebrowBanner.types';

export const OffersCountdownEyebrowBannerComp: FC<OffersCountdownEyebrowBannerPresentationProps> = memo(props => {
  const { aBody, mTimer, authorMode, expirationDate } = props;

  // Extract labels from mTimer
  const daysLabel = mTimer?.aBody?.bodyText;
  const hoursLabel = mTimer?.aBody2?.bodyText;
  const minutesLabel = mTimer?.aBody3?.bodyText;
  const secondsLabel = mTimer?.aBody4?.bodyText;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const lastPoliteAnnouncementRef = useRef('');
  const hasAnnouncedInitialTimerRef = useRef(false);
  const [initialTimerAnnouncement, setInitialTimerAnnouncement] = useState('');
  const promoBodyText = aBody?.bodyText;
  const promoHasOmittedTags = /<(del|strike|s)\b/i.test(promoBodyText ?? '');
  const accessiblePromoText = useMemo(
    () => (promoHasOmittedTags ? getAccessiblePromoText(promoBodyText) : ''),
    [promoBodyText, promoHasOmittedTags]
  );
  // Initialize synchronously so an already-expired timer never renders a visible frame before it is hidden.
  const [isExpired, setIsExpired] = useState(() => isTargetDateExpired(expirationDate));
  const onComplete = () => {
    setIsExpired(true);
  };

  const hideTimerStaticSummary = () => {
    const timerRoot = wrapperRef.current?.querySelector<HTMLElement>(
      '[data-testid="web-ui-kit-MTimer"], [data-testid="mtimer-component"]'
    );

    if (!timerRoot) {
      return;
    }

    const staticSummaryNode = Array.from(timerRoot.querySelectorAll<HTMLElement>('*')).find(
      node => node.getAttribute('aria-live') !== 'polite' && node.textContent?.includes('remaining')
    );

    if (staticSummaryNode) {
      staticSummaryNode.setAttribute('aria-hidden', 'true');
    }
  };

  useEffect(() => {
    setInitialTimerAnnouncement('');
    hasAnnouncedInitialTimerRef.current = false;
  }, [authorMode]);

  useEffect(() => {
    if (authorMode || hasAnnouncedInitialTimerRef.current) {
      return;
    }

    const nextAnnouncement = getAccessibleTimerAnnouncement(expirationDate);

    if (!nextAnnouncement) {
      return;
    }

    setInitialTimerAnnouncement(nextAnnouncement);
    hasAnnouncedInitialTimerRef.current = true;
  }, [authorMode, expirationDate]);

  useEffect(() => {
    hideTimerStaticSummary();
  }, [initialTimerAnnouncement]);

  useEffect(() => {
    if (!wrapperRef.current) {
      return;
    }

    const observer = new MutationObserver(() => {
      hideTimerStaticSummary();
      const politeNode = wrapperRef.current?.querySelector<HTMLElement>(
        '[data-testid="web-ui-kit-MTimer"] [aria-live="polite"], [data-testid="mtimer-component"] [aria-live="polite"]'
      );
      const politeText = politeNode?.textContent?.trim().replace(/\s+/g, ' ') || '';

      if (!politeText || politeText === lastPoliteAnnouncementRef.current) {
        return;
      }

      lastPoliteAnnouncementRef.current = politeText;
      setInitialTimerAnnouncement('');
    });

    observer.observe(wrapperRef.current, {
      childList: true,
      characterData: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  if (isExpired && !authorMode) {
    return null;
  }

  return (
    <Styled.Wrapper ref={wrapperRef} data-author-mode={authorMode ? 'true' : 'false'}>
      <div className="container py-2">
        <div className="row align-items-center">
          <div
            className={clsx(
              'col-12',
              'col-lg-auto',
              'd-flex',
              'justify-content-center',
              'justify-content-lg-start',
              'mb-lg-0',
              'offers_countdown-timer'
            )}
          >
            <MTimer
              className="offers_countdown-timer"
              targetDate={expirationDate || ''}
              daysLabel={daysLabel}
              hoursLabel={hoursLabel}
              minutesLabel={minutesLabel}
              secondsLabel={secondsLabel}
              customDigitClass={'offers_countdown_digit'}
              isAuthorMode={authorMode}
              onComplete={onComplete}
            />
            {initialTimerAnnouncement ? (
              <span
                className="sr-only"
                data-testid="countdown-banner-live-region"
                aria-live="polite"
                aria-atomic="true"
              >
                {initialTimerAnnouncement}
              </span>
            ) : null}
          </div>

          <div className="col-12 col-lg-auto text-center text-lg-end mb-lg-0 offers_countdown-body">
            {aBody?.bodyText && (
              <>
                <ABody
                  size={aBody?.size ?? ABodySize.M}
                  color={aBody?.color ?? ABodyColor.Default}
                  bodyText={aBody?.bodyText}
                  className={clsx('pb-0', 'offers_countdown-body')}
                  aria-hidden={promoHasOmittedTags ? true : undefined}
                />
                {promoHasOmittedTags && accessiblePromoText ? (
                  <span className="sr-only" data-testid="countdown-banner-accessible-promo">
                    {accessiblePromoText}
                  </span>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>
    </Styled.Wrapper>
  );
});

OffersCountdownEyebrowBannerComp.displayName = 'OffersCountdownEyebrowBannerComp';
